// Making the page installable, and keeping an installed one up to date.
//
// A service worker is what turns a tab into something a phone will keep on its
// home screen. It is also what makes an installed app *stick* on the version it
// was installed with, which is the failure this file mostly exists to prevent:
// reinstalling from the home screen to pick up a new build means signing in to
// Discord again, so an app that cannot update itself is worse than no app.
//
// The caching policy lives in `web/sw.js`. What lives here is the handover.

/**
 * Whether a new version may be applied without asking.
 *
 * True until a match starts. Reloading during boot or on the deck screen costs
 * nothing; reloading in the middle of someone's turn throws away a local game
 * and drops a remote one, so from then on the update waits behind a button.
 */
let mayReload = true;

/** Called once a game is under way. From here an update has to ask. */
export function holdUpdatesDuringPlay(): void {
  mayReload = false;
}

/** Set once, so the two paths that can trigger a reload cannot both take it. */
let reloading = false;

/** Offers the update as a button rather than taking it. */
function offer(apply: () => void): void {
  const bar = document.getElementById('update');
  const button = document.getElementById('update-now');
  if (!bar || !button) { apply(); return; }
  bar.hidden = false;
  button.addEventListener('click', apply, { once: true });
}

/**
 * Registers the worker and wires up how a new one takes over.
 *
 * Never throws. A failed registration means the game is not installable; it
 * does not mean the game does not run, and the two must not be confused.
 */
export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  // A worker cannot be registered from a file:// page or over plain http, and
  // asking anyway logs a console error that looks like a bug.
  if (!globalThis.isSecureContext) return;

  // Read *before* registering. On a first visit there is no controller, and
  // the one that arrives is not an update -- reloading for it would be a
  // pointless reload on everybody's first launch.
  const hadController = !!navigator.serviceWorker.controller;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

    /** Tells a worker that has finished installing to take over now. */
    const takeOver = (worker: ServiceWorker): void => {
      if (mayReload) worker.postMessage({ type: 'skip-waiting' });
      else offer(() => worker.postMessage({ type: 'skip-waiting' }));
    };

    // The case that was missing, and the reason an installed app stayed on the
    // version it was installed with. A worker that finished installing during
    // some *earlier* visit is already sitting in `waiting` when the page loads
    // -- `updatefound` fired last time, not this time -- and without this
    // nothing ever tells it to activate. It waits for every client to close,
    // which for a home-screen app can be weeks.
    if (registration.waiting) takeOver(registration.waiting);

    registration.addEventListener('updatefound', () => {
      const next = registration.installing;
      if (!next) return;
      next.addEventListener('statechange', () => {
        // `controller` distinguishes an update from a first install: with no
        // controller this worker is not replacing anything.
        if (next.state === 'installed' && navigator.serviceWorker.controller) takeOver(next);
      });
    });

    // A new worker controlling the page does not change the page: the old
    // build's JavaScript is already running and will go on running until it is
    // reloaded. This is the half that was missing.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController || reloading) return;
      reloading = true;
      if (mayReload) location.reload();
      else offer(() => location.reload());
    });

    // An installed app is usually *resumed*, not launched: on a phone it sits
    // in the background for days and comes back without a page load, so a
    // check that only runs at startup runs about once a week. Coming back to
    // the foreground is the moment to ask again.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) void registration.update().catch(() => {});
    });

    await registration.update().catch(() => {});
  } catch (err) {
    console.warn('service worker not registered:', err);
  }
}

/** When this build was made. Replaced at build time by Vite's `define`. */
declare const __BUILD__: string;

/** The build stamp, or a placeholder in dev where nothing defined it. */
export function buildStamp(): string {
  return typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';
}

/** True when the page is running as an installed app rather than in a tab. */
export function installed(): boolean {
  return globalThis.matchMedia?.('(display-mode: standalone)').matches === true
    // iOS predates the media query and has never implemented it.
    || (globalThis.navigator as { standalone?: boolean }).standalone === true;
}
